import { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import InviteToMoveInInvitationModal from './InviteToMoveInInvitationModal';
import InviteToMoveInConfirmationModal from './InviteToMoveInConfirmationModal';

import useQueryVariables from '../../../hooks/useQueryVariables';
import {
  getInvitationInfo,
  getIsAcceptedOrInvitedToMoveIn,
  INVITE_TO_MOVE_IN_MUTATION,
  INVITE_TO_MOVE_IN_QUERY,
} from '../../../utils/inviteToMoveIn';
import { getDefaultInvitationText } from './InviteToMoveInManagerUtil';
import {
  getIsIncomeToRentEligible,
  getIsRentGuaranteeEnabled,
  SEND_INELIGIBLE_TENANT_INVITED_NOTIFICATION,
} from '../../../utils/rentGuarantee';
import { useSelector } from 'react-redux';
import { listingAliasSelector } from '../../../selectors';
import TakeActionDropdown from './TakeActionDropdown';
import { useApplicationSummaryContext } from '../../../contexts/ApplicationSummaryContext';
import useQueryParams from '../../../hooks/useQueryParams';
import { useListingUser } from '../../../hooks/useListingUser';
import { emitOpenConfirmationModalInvitationModalEvent } from '../../../../client/analytics/clickstreamEvents';

const INVITATION_ERROR_MESSAGE = 'Something went wrong. Please try again or double-check your connection.';

function InviteToMoveInManager() {
  const { refetchApplication } = useApplicationSummaryContext();
  const { applicationId } = useQueryVariables();
  const listingAlias = useSelector(listingAliasSelector);
  const { reviewApplicationStep } = useQueryParams();
  const { data, loading, error } = useQuery(INVITE_TO_MOVE_IN_QUERY, {
    variables: { propertyId: listingAlias, applicationId },
  });
  const [inviteUserToMoveIn] = useMutation(INVITE_TO_MOVE_IN_MUTATION);
  const [sendIneligibleTenantInvitedNotification] = useMutation(SEND_INELIGIBLE_TENANT_INVITED_NOTIFICATION);
  const isRentGuaranteeEnabled = getIsRentGuaranteeEnabled(data?.property);
  const isIncomeToRentEligible = getIsIncomeToRentEligible(data?.property);
  const { applicants, contactName, street } = getInvitationInfo(data);
  const rentGuaranteeLegalDisclaimers = data?.property?.rentGuarantee?.legalDisclaimerContent?.disclaimers;
  const { user, listing } = useListingUser();
  const primaryApplicant = applicants.find((applicant) => applicant.isPrimaryApplicant);
  const [hasSentAcceptedRentProtectionIneligibleTenantsEmail, setHasSentAcceptedRentProtectionIneligibleTenantsEmail] =
    useState(false);
  /**
   * Manage email text here to have it persist even when the user closes out
   * of the invitation modal.
   */
  const [emailText, setEmailText] = useState('');

  /**
   * Triggers can't handle opening modals from other modals, so we'll have to
   * do this manually.
   */
  const [isInvitationModalOpen, setIsInvitationModalOpen] = useState(Boolean(reviewApplicationStep));
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [invitationModalErrorMessage, setInvitationModalErrorMessage] = useState('');

  function closeInvitationModal() {
    setIsInvitationModalOpen(false);
    setInvitationModalErrorMessage('');
  }

  function handleEmailTextChange(e) {
    setEmailText(e.target.value);
  }

  async function sendInvitation() {
    if (isRentGuaranteeEnabled && !isIncomeToRentEligible && !hasSentAcceptedRentProtectionIneligibleTenantsEmail) {
      let success;
      await sendIneligibleTenantInvitedNotification({
        variables: { listingAlias },
      })
        .then(({ data: responseData }) => {
          success = responseData.sendIneligibleTenantInvitedNotification.success;
          setHasSentAcceptedRentProtectionIneligibleTenantsEmail(true);
        })
        .catch(() => {
          setInvitationModalErrorMessage(INVITATION_ERROR_MESSAGE);
          success = false;
        });

      // prevent call to ITMI if ineligible tenant invitation email fails
      if (!success) {
        return;
      }
    }

    await inviteUserToMoveIn({
      variables: {
        propertyId: listingAlias,
        applicationId,
        emailText,
      },
    })
      .then(({ data: responseData }) => {
        if (getIsAcceptedOrInvitedToMoveIn(responseData)) {
          closeInvitationModal();
          setIsConfirmationModalOpen(true);
          emitOpenConfirmationModalInvitationModalEvent(listing, user);
          refetchApplication();
        } else {
          throw new Error('Invitation to move in failed to send.');
        }
      })
      .catch(() => {
        setInvitationModalErrorMessage(INVITATION_ERROR_MESSAGE);
      });
  }

  useEffect(() => {
    setEmailText(
      getDefaultInvitationText({
        applicants,
        contactName,
        street,
      }),
    );
  }, [applicants, contactName, street]);

  return (
    !loading &&
    !error &&
    primaryApplicant?.submittedDate && (
      <>
        <TakeActionDropdown
          {...{
            onApproveApplicationClickCallback: () => setIsInvitationModalOpen(true),
          }}
        />
        <InviteToMoveInInvitationModal
          {...{
            isOpen: isInvitationModalOpen,
            onClose: closeInvitationModal,
            emailText,
            invitationModalErrorMessage,
            handleEmailTextChange,
            sendInvitation,
          }}
        />
        <InviteToMoveInConfirmationModal
          {...{
            isOpen: isConfirmationModalOpen,
            onClose: () => setIsConfirmationModalOpen(false),
            isIncomeToRentEligible,
            rentGuaranteeLegalDisclaimers,
          }}
        />
      </>
    )
  );
}

export default InviteToMoveInManager;
